/**
 * ContractRiskCard — surfaces the consolidated risk score for the
 * currently-selected quotation. Compact in collapsed state (badge +
 * bucket + score), expands to show the 5-component breakdown so the
 * CFO sees the **reasoning chain**, not just the verdict.
 */
import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { ShieldAlert, ShieldCheck, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

const BUCKET_META = {
  low:      { label: 'Low Risk',      cls: 'bg-emerald-50 text-emerald-800 border-emerald-200', Icon: ShieldCheck },
  medium:   { label: 'Medium Risk',   cls: 'bg-amber-50 text-amber-800 border-amber-200',     Icon: ShieldAlert },
  high:     { label: 'High Risk',     cls: 'bg-orange-50 text-orange-800 border-orange-200',   Icon: AlertTriangle },
  critical: { label: 'Critical Risk', cls: 'bg-red-50 text-red-800 border-red-200',           Icon: AlertTriangle },
};

const fmtSAR = (n) => `${Number(n || 0).toLocaleString()} SAR`;

export default function ContractRiskCard({ quotation, compact = false }) {
  const [risk, setRisk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!quotation?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axiosInstance.get(
          `/quotations/${quotation.id}/risk`
        );
        if (!cancelled) setRisk(data);
      } catch (_) {
        if (!cancelled) setRisk(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [quotation?.id]);

  if (loading || !risk) return null;
  const meta = BUCKET_META[risk.bucket] || BUCKET_META.medium;
  const Icon = meta.Icon;

  if (compact) {
    return (
      <span
        data-testid={`contract-risk-badge-${risk.bucket}`}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold border ${meta.cls}`}
        title={`Risk score ${risk.score}/100`}
      >
        <Icon className="w-3 h-3" />
        {meta.label} · {risk.score}
      </span>
    );
  }

  return (
    <div
      data-testid="contract-risk-card"
      className={`mb-3 rounded-lg border ${meta.cls} p-3 text-sm`}
    >
      <button
        onClick={() => setExpanded((v) => !v)}
        data-testid="contract-risk-toggle"
        className="w-full flex items-center justify-between gap-2"
      >
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" />
          <span className="font-semibold">Contract Risk</span>
          <span
            data-testid={`contract-risk-bucket-${risk.bucket}`}
            className="font-mono text-xs px-2 py-0.5 rounded bg-white/60"
          >
            {meta.label}
          </span>
          <span data-testid="contract-risk-score" className="font-bold">
            {risk.score} / 100
          </span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {expanded && (
        <div
          data-testid="contract-risk-breakdown"
          className="mt-3 pt-3 border-t border-current/20 space-y-2"
        >
          {risk.components.map((c) => {
            const pct = c.weight > 0 ? (c.points / c.weight) * 100 : 0;
            return (
              <div
                key={c.key}
                data-testid={`risk-component-${c.key}`}
                className="text-xs"
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{c.label}</span>
                  <span className="font-mono">
                    {c.points.toFixed(1)} / {c.weight.toFixed(0)}
                  </span>
                </div>
                <div className="h-1.5 mt-1 bg-white/40 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-current opacity-60"
                    style={{ width: `${Math.min(pct, 100)}%` }}
                  />
                </div>
                {/* Single-line reason summary, tailored per component. */}
                <div className="opacity-70 mt-1">
                  {c.key === 'discount' && (
                    <>R0 {fmtSAR(c.detail.r0_total)} → latest {fmtSAR(c.detail.latest_total)} ·
                    {' '}{(c.detail.discount_pct * 100).toFixed(1)}% concession</>
                  )}
                  {c.key === 'revisions' && (
                    <>{c.detail.n_revisions} revision{c.detail.n_revisions === 1 ? '' : 's'} in this chain</>
                  )}
                  {c.key === 'aggressiveness' && (
                    <>Customer aggressiveness {Number(c.detail.aggressiveness_score).toFixed(0)}/100
                    {' '}· {c.detail.n_chains} chain{c.detail.n_chains === 1 ? '' : 's'}
                    {' '}· avg {c.detail.avg_revisions} revisions</>
                  )}
                  {c.key === 'collection' && (
                    <>Collection probability {(c.detail.probability * 100).toFixed(0)}%
                    {' '}· {c.detail.invoices ?? 0} prior invoice{c.detail.invoices === 1 ? '' : 's'}</>
                  )}
                  {c.key === 'exposure' && (
                    <>{fmtSAR(c.detail.amount)} of {fmtSAR(c.detail.cap)} cap</>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
